import { useState } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/base/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功，我们会尽快与你联系！');
      form.reset();
      return;
    }

    formData.delete('company_alt');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功，我们会尽快与你联系！');
        form.reset();
      } else {
        setFormStatus('error');
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于911吃瓜网',
    description: '911吃瓜网是国内领先的网红娱乐资讯聚合平台，致力于为吃瓜群众提供最新、最全的网红塌房、明星八卦、社会奇闻等热门黑料内容。',
    url: 'http://51cgwang.cn/about',
    mainEntity: {
      '@type': 'Organization',
      name: '911吃瓜网',
      url: 'http://51cgwang.cn/',
      description: '国内领先的网红娱乐资讯聚合平台',
      foundingDate: '2024',
      email: 'hello@51cgwang.cn',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'business cooperation',
        email: 'hello@51cgwang.cn',
        availableLanguage: ['Chinese'],
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://51cgwang.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '911吃瓜网是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '911吃瓜网是国内领先的网红娱乐资讯聚合平台，专注于网红塌房、明星八卦、社会奇闻等热门黑料内容的实时追踪与独家爆料。我们致力于为吃瓜群众提供第一手、高质量的娱乐资讯内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向911吃瓜网投稿爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '你可以通过本页面的投稿表单提交爆料内容，也可以发送邮件至 hello@51cgwang.cn。我们严格保护爆料人隐私，所有投稿均采用匿名处理流程，确保你的个人信息安全。',
        },
      },
      {
        '@type': 'Question',
        name: '911吃瓜网的内容是否可靠？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '911吃瓜网坚持"快、准、深"的内容原则。所有独家爆料均经过多源交叉核实，并标注信息来源。对于暂未完全证实的传闻，我们会明确标注"待核实"标签，让读者了解信息的可信度等级。',
        },
      },
      {
        '@type': 'Question',
        name: '如何与911吃瓜网进行商务合作？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '商务合作请发送邮件至 hello@51cgwang.cn，或通过本页面的联系方式表单提交合作意向。我们的商务团队会在1-3个工作日内与你取得联系。',
        },
      },
    ],
  };

  return (
    <>
      <PageSEO
        title="关于我们 - 911吃瓜网 | 品牌介绍与联系方式"
        description="911吃瓜网是国内领先的网红娱乐资讯聚合平台。了解我们的品牌故事、平台口碑、联系方式，欢迎投稿爆料或商务合作。"
        keywords="911吃瓜网,关于我们,联系方式,投稿爆料,网红黑料平台"
        canonicalUrl="http://51cgwang.cn/about"
      />
      <script type="application/ld+json">
        {JSON.stringify(aboutSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <Navbar />

      <main>
        <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
          <div className="absolute inset-0 mx-4 md:mx-6 my-3 md:my-4 rounded-3xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20network%20of%20connections%20and%20magnifying%20glass%20concept%2C%20dark%20warm%20tones%2C%20mysterious%20atmosphere%2C%20digital%20investigation%20theme%2C%20cinematic%20photography%2C%20professional%20lighting%2C%20bold%20composition&width=1600&height=800&seq=about-hero-01&orientation=landscape"
              alt="关于911吃瓜网"
              title="911吃瓜网 - 让真相先走一步"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/25 to-black/55"></div>
          </div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 w-full">
            <span className="inline-block px-4 py-1.5 border border-white/40 text-white text-sm rounded-full mb-4 backdrop-blur-sm whitespace-nowrap">
              关于我们
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-3">
              让真相先走一步
            </h1>
            <p className="text-sm text-white/70 max-w-md">
              国内领先的网红娱乐资讯聚合平台
            </p>
          </div>
        </section>

        <section className="py-10 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '关于我们' },
              ]}
            />
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-gradient-to-b from-foreground-900 to-foreground-950 text-white">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-primary-500/20 text-primary-300 text-xs rounded-full mb-4 whitespace-nowrap">
                品牌故事 / STORY
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-4 max-w-2xl">
                重新定义吃瓜体验
              </h2>
              <div className="text-sm md:text-base text-foreground-300 max-w-2xl leading-relaxed space-y-3">
                <p>
                  911吃瓜网诞生于2024年初，由一群热爱追踪网红娱乐圈动态的资深"吃瓜群众"创立。我们深知在信息泛滥的时代，找到真实、及时、有料的娱乐资讯有多么困难——大量营销号制造虚假信息，主流媒体又往往慢半拍。
                </p>
                <p>
                  因此，911吃瓜网确立了<strong className="text-white">"快、准、深"</strong>三大核心原则：比别人更快发现热点，比别人更准核实信息，比别人更深追踪事件。我们组建了专业的编辑团队和广泛的爆料网络，确保每一条内容都经得起推敲。
                </p>
                <p>
                  截至目前，911吃瓜网已累计收录超过五万条爆料内容，覆盖网红塌房、明星八卦、社会奇闻等多个领域，日均访问量突破百万，已成为国内最有影响力的吃瓜资讯平台之一。
                </p>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-center gap-10 mt-12">
              <div className="w-full lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20open%20office%20workspace%20with%20multiple%20monitors%20showing%20news%20feeds%2C%20warm%20lighting%2C%20collaborative%20team%20environment%2C%20editorial%20photography%2C%20clean%20aesthetic%2C%20professional%20atmosphere&width=800&height=600&seq=about-team-02&orientation=landscape"
                  alt="911吃瓜网团队"
                  title="911吃瓜网 - 专业编辑团队"
                  className="w-full rounded-2xl object-cover"
                  loading="lazy"
                />
              </div>
              <div className="w-full lg:w-1/2">
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { num: '50,000+', label: '累计收录爆料' },
                    { num: '100万+', label: '日均访问量' },
                    { num: '4', label: '核心内容板块' },
                    { num: '24/7', label: '全天候追踪' },
                  ].map((stat) => (
                    <div key={stat.label}>
                      <div className="text-2xl md:text-3xl font-heading font-bold text-white mb-1">
                        {stat.num}
                      </div>
                      <p className="text-xs text-foreground-400">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 border border-foreground-200 text-foreground-600 text-xs rounded-full mb-4 whitespace-nowrap">
                平台数据 / DATA
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                数字背后的911吃瓜网
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                每一个数字，都是用户对我们的信任与认可
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
              {[
                { num: '50,000+', label: '累计爆料', icon: 'ri-file-text-line', desc: '海量内容持续更新' },
                { num: '100万+', label: '日均访问', icon: 'ri-user-line', desc: '千万瓜友共同选择' },
                { num: '4', label: '内容板块', icon: 'ri-layout-grid-line', desc: '全方位覆盖热点' },
                { num: '98.7%', label: '准确率', icon: 'ri-check-double-line', desc: '多源交叉核实' },
                { num: '3,200+', label: '独家爆料', icon: 'ri-lock-line', desc: '别处看不到的猛料' },
                { num: '< 5分钟', label: '响应速度', icon: 'ri-timer-flash-line', desc: '热点事件极速追踪' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="group p-5 rounded-xl bg-background-50 border border-background-200/70 text-center hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 cursor-default"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-3 group-hover:bg-primary-200 transition-colors">
                    <i className={`${stat.icon} text-lg text-primary-500`}></i>
                  </div>
                  <div className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-1">
                    {stat.num}
                  </div>
                  <p className="text-xs font-semibold text-foreground-700 mb-1">{stat.label}</p>
                  <p className="text-xs text-foreground-400">{stat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 border border-foreground-200 text-foreground-600 text-xs rounded-full mb-4 whitespace-nowrap">
                发展历程 / TIMELINE
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                品牌成长里程碑
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                从零到百万，每一步都走得扎实
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-0.5 hidden md:block"></div>

              <div className="space-y-8 md:space-y-0">
                {[
                  {
                    date: '2024年1月',
                    title: '平台正式上线',
                    desc: '911吃瓜网正式面向公众开放，初期仅覆盖网红塌房单一板块，首月UV突破10万。',
                    icon: 'ri-rocket-line',
                  },
                  {
                    date: '2024年3月',
                    title: '明星八卦板块上线',
                    desc: '应广大瓜友呼声，正式推出明星八卦频道，内容覆盖范围扩大至娱乐圈全领域。',
                    icon: 'ri-star-line',
                  },
                  {
                    date: '2024年6月',
                    title: '独家黑料频道成立',
                    desc: '组建专业调查团队，开启独家原创内容生产，首篇独家爆料即获千万级阅读量。',
                    icon: 'ri-lock-line',
                  },
                  {
                    date: '2024年9月',
                    title: '日均访问突破50万',
                    desc: '凭借高质量内容和精准热点追踪，平台流量高速增长，跻身行业第一梯队。',
                    icon: 'ri-line-chart-line',
                  },
                  {
                    date: '2025年1月',
                    title: '社会奇闻板块上线',
                    desc: '四大板块格局成型，覆盖网红、明星、社会、独家全方位内容生态。',
                    icon: 'ri-fire-line',
                  },
                  {
                    date: '2025年6月',
                    title: '日均访问突破100万',
                    desc: '成为国内最具影响力的吃瓜资讯平台之一，累计服务超过千万独立用户。',
                    icon: 'ri-trophy-line',
                  },
                ].map((item, i) => (
                  <div
                    key={item.date}
                    className={`relative flex flex-col md:flex-row items-start gap-4 md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                        <span className="inline-block px-2.5 py-1 bg-primary-100 text-primary-600 text-xs rounded-full mb-3 whitespace-nowrap">
                          {item.date}
                        </span>
                        <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>

                    <div className="hidden md:flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center border-4 border-background-100 z-10">
                        <i className={`${item.icon} text-white text-sm`}></i>
                      </div>
                    </div>

                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 border border-foreground-200 text-foreground-600 text-xs rounded-full mb-4 whitespace-nowrap">
                核心能力 / CAPABILITY
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                为什么选择911吃瓜网
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                四大核心能力，为你打造最好的吃瓜体验
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  icon: 'ri-timer-flash-line',
                  title: '实时追踪',
                  desc: '7×24小时全网监控，第一时间发现并报道热点事件，绝不错过任何一个大瓜。',
                },
                {
                  icon: 'ri-global-line',
                  title: '全网聚合',
                  desc: '整合各大社交平台、论坛、爆料渠道，一站式浏览所有热门黑料内容。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '真相核查',
                  desc: '专业编辑团队多源交叉核实，标注信息可信度，让每一条爆料都有据可查。',
                },
                {
                  icon: 'ri-lock-line',
                  title: '隐私保护',
                  desc: '采用匿名投稿机制，严格保护爆料人隐私，让你的声音安全传达。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="group p-6 rounded-xl bg-background-50 border border-background-200/70 hover:bg-primary-500 hover:border-primary-500 transition-all duration-300 cursor-default"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-background-100 rounded-lg mb-4 group-hover:bg-white/20 transition-colors">
                    <i className={`${item.icon} text-xl text-primary-500 group-hover:text-white transition-colors`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 group-hover:text-white mb-2 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 group-hover:text-white/80 transition-colors leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-600 text-xs rounded-full mb-4 whitespace-nowrap">
                商务合作 / COOPERATION
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                与911吃瓜网携手共赢
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                多种合作模式，期待与你共创价值
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              {[
                {
                  icon: 'ri-advertisement-line',
                  title: '广告投放',
                  desc: '精准触达千万级吃瓜用户群体，提供开屏广告、信息流投放、品牌专区等多种广告形式，ROI远超行业平均水平。',
                  highlight: '日均曝光 2000万+',
                },
                {
                  icon: 'ri-shake-hands-line',
                  title: '内容共创',
                  desc: '与品牌方深度合作，打造原生种草内容。从选题策划到内容生产，专业编辑团队全程护航，实现品效合一。',
                  highlight: '合作品牌 200+',
                },
                {
                  icon: 'ri-survey-line',
                  title: '舆情监测',
                  desc: '基于平台海量数据和AI分析能力，为企业提供网红舆情监测、竞品分析、危机预警等数据服务。',
                  highlight: '覆盖KOL 10万+',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="group p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-default"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4 group-hover:bg-primary-500 transition-colors">
                    <i className={`${item.icon} text-xl text-primary-500 group-hover:text-white transition-colors`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-4">{item.desc}</p>
                  <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full whitespace-nowrap">
                    {item.highlight}
                  </span>
                </div>
              ))}
            </div>

            <div className="p-6 md:p-8 rounded-2xl bg-foreground-950 text-white">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10">
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-bold text-white mb-3">
                    立即联系商务团队
                  </h3>
                  <p className="text-sm text-foreground-300 leading-relaxed mb-0">
                    我们的商务团队会在收到邮件后的<strong className="text-white">1-3个工作日</strong>内与你取得联系，为你量身定制合作方案。
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-foreground-800 border border-foreground-700">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-500/20 rounded-lg">
                      <i className="ri-mail-line text-lg text-primary-400"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">商务合作邮箱</p>
                      <a href="mailto:hello@51cgwang.cn" className="text-sm text-white font-medium hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
                        hello@51cgwang.cn
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-foreground-800 border border-foreground-700">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-500/20 rounded-lg">
                      <i className="ri-phone-line text-lg text-primary-400"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">工作时间</p>
                      <p className="text-sm text-white font-medium whitespace-nowrap">周一至周五 9:00-18:00</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="flex flex-col lg:flex-row">
          <div className="w-full lg:w-1/2 relative h-[300px] md:h-[450px] overflow-hidden bg-background-100">
            <img
              src="https://readdy.ai/api/search-image?query=Mysterious%20person%20typing%20on%20laptop%20in%20dark%20room%2C%20dramatic%20lighting%20from%20screen%2C%20anonymous%20whistleblower%20concept%2C%20cinematic%20photography%2C%20dark%20warm%20tones%2C%20investigative%20mood&width=900&height=700&seq=about-cta-03&orientation=landscape"
              alt="投稿爆料 - 911吃瓜网"
              title="向911吃瓜网投稿爆料"
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent"></div>
            <div className="absolute bottom-8 left-8">
              <span className="text-xs text-white/60 mb-2 block">加入我们</span>
              <p className="text-xl md:text-2xl font-heading font-bold text-white max-w-xs leading-tight">
                有料要爆？我们洗耳恭听
              </p>
            </div>
          </div>

          <div className="w-full lg:w-1/2 py-14 px-8 bg-foreground-950 text-white flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              联系我们
            </h2>
            <p className="text-sm text-foreground-300 max-w-md mb-8 leading-relaxed">
              无论是投稿爆料、商务合作还是反馈建议，我们都期待收到你的消息。你的每一条信息都会被认真对待。
            </p>

            {formStatus === 'success' ? (
              <div className="w-full max-w-md text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-green-500/20 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-green-400"></i>
                </div>
                <p className="text-white font-semibold mb-1">提交成功！</p>
                <p className="text-sm text-foreground-300">{formMessage}</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-6 px-5 py-2 border border-white/30 text-white text-sm rounded-full hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
                >
                  继续提交
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
               
                onSubmit={handleSubmit}
                action="http://51cgwang.cn/"
                className="w-full max-w-md space-y-4"
              >
                <div className="anti-spam-field">
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-300 mb-1.5 text-left">
                    你的昵称
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 bg-foreground-800 border border-foreground-700 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="请输入你的昵称"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-300 mb-1.5 text-left">
                    电子邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 bg-foreground-800 border border-foreground-700 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="请输入你的邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="contact-type" className="block text-sm text-foreground-300 mb-1.5 text-left">
                    联系类型
                  </label>
                  <select
                    id="contact-type"
                    name="contact_type"
                    required
                    className="w-full px-4 py-2.5 bg-foreground-800 border border-foreground-700 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                  >
                    <option value="">请选择联系类型</option>
                    <option value="爆料投稿">爆料投稿</option>
                    <option value="商务合作">商务合作</option>
                    <option value="反馈建议">反馈建议</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-300 mb-1.5 text-left">
                    详细内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-foreground-800 border border-foreground-700 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    placeholder="请详细描述你的爆料内容或合作意向（限500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1 text-right">限500字</p>
                </div>

                {formStatus === 'error' && (
                  <p className="text-sm text-red-400 text-center">{formMessage}</p>
                )}

                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  提交信息
                </button>

                <p className="text-xs text-foreground-500 text-center">
                  提交即表示你同意我们的
                  <a href="#" className="text-primary-400 hover:underline cursor-pointer">隐私政策</a>
                  和
                  <a href="#" className="text-primary-400 hover:underline cursor-pointer">用户协议</a>
                </p>
              </form>
            )}
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                {
                  q: '911吃瓜网是什么平台？',
                  a: '911吃瓜网是国内领先的网红娱乐资讯聚合平台，专注于网红塌房、明星八卦、社会奇闻等热门黑料内容的实时追踪与独家爆料。我们致力于为吃瓜群众提供第一手、高质量的娱乐资讯内容。',
                },
                {
                  q: '如何向911吃瓜网投稿爆料？',
                  a: '你可以通过本页面的投稿表单提交爆料内容，也可以发送邮件至 hello@51cgwang.cn。我们严格保护爆料人隐私，所有投稿均采用匿名处理流程，确保你的个人信息安全。',
                },
                {
                  q: '911吃瓜网的内容是否可靠？',
                  a: '911吃瓜网坚持"快、准、深"的内容原则。所有独家爆料均经过多源交叉核实，并标注信息来源。对于暂未完全证实的传闻，我们会明确标注"待核实"标签，让读者了解信息的可信度等级。',
                },
                {
                  q: '如何与911吃瓜网进行商务合作？',
                  a: '商务合作请发送邮件至 hello@51cgwang.cn，或通过本页面的联系方式表单提交合作意向。我们的商务团队会在1-3个工作日内与你取得联系。',
                },
                {
                  q: '911吃瓜网是否保护用户隐私？',
                  a: '是的，用户隐私保护是我们的核心原则之一。所有投稿人均受到严格的匿名保护，我们不会向任何第三方透露投稿人的个人信息。同时，网站浏览数据仅用于优化内容推荐，不会被用于其他商业用途。',
                },
                {
                  q: '我可以在其他平台转发911吃瓜网的内容吗？',
                  a: '欢迎在保留出处和原文链接的前提下转发我们的内容。如需商业转载或大量引用，请通过商务合作渠道联系我们获取授权。',
                },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}